import { useAtom, useAtomValue } from 'infrastructure/state/jotai.wrapper'
import {
  alignmentCountsAtom,
  alignmentInputsAtom,
  currentCrosswalkIdAtom,
  newCrosswalkDataAtom,
  parsedCurrentSheetAtom,
} from './atoms'
import {
  useAllCrosswalks,
  useAllFrameworks,
  useCommonCoreFrameworks,
  useGetFrameworkById,
} from 'domains/scope-and-sequence/hooks'
import { useCallback, useEffect, useMemo } from 'react'
import { currentUserWithRefresh } from 'infrastructure/auth/auth.atoms'
import { maintainCrosswalkAlignments } from 'domains/scope-and-sequence/api'
import { useWithErrorNotification } from 'shared/ui/GlobalDialog'
import { alignmentKey } from './utility'
import { FrameworkType } from 'domains/scope-and-sequence/types'

export function useNewCrosswalkData() {
  const [data, setData] = useAtom(newCrosswalkDataAtom)
  const user = useAtomValue(currentUserWithRefresh)
  const getter = useGetFrameworkById()
  const allFrameworks = useAllFrameworks()
  const parsedData = useAtomValue(parsedCurrentSheetAtom)
  const commonCore = useCommonCoreFrameworks()

  useEffect(() => {
    setData((prev) => ({
      ...prev,
      created_by: String(user.id),
    }))
  }, [setData, user.id])

  useEffect(() => {
    if (!allFrameworks || !parsedData) return
    const subject = parsedData.subject
    const sourceName = parsedData.frameworks.source_framework_id
    const subjectFind = (f: FrameworkType) => {
      const frameworkSubj = f.name.split(' - ').pop()!
      return (
        subject && (f.name.includes(subject) || subject.includes(frameworkSubj))
      )
    }
    if (sourceName) {
      const maybeSourceState = sourceName.split(' ')[0]
      const filtered = allFrameworks.filter((f) =>
        f.name.includes(maybeSourceState),
      )
      const forSubject = filtered.find(subjectFind) ?? filtered[0]
      if (forSubject) {
        setData((prev) => ({
          ...prev,
          source_framework_id: forSubject.id,
        }))
      }
    }

    if (commonCore) {
      const ccFramework = commonCore.find(subjectFind)
      if (ccFramework) {
        setData((prev) => ({
          ...prev,
          target_framework_id: ccFramework.id,
        }))
      }
    }
  }, [allFrameworks, setData, parsedData, commonCore])

  return useMemo(() => {
    return {
      data,
      setData,
      sourceFramework:
        data.source_framework_id && getter(data.source_framework_id),
      targetFramework:
        data.target_framework_id && getter(data.target_framework_id),
      userName: user.name,
    }
  }, [data, getter, user.name, setData])
}

// All available crosswalks for this source/target pair
export function useCurrentCrosswalks() {
  const {
    data: {
      source_framework_id: fromFramework,
      target_framework_id: toFramework,
    },
  } = useNewCrosswalkData()
  const crosswalks = useAllCrosswalks()

  return useMemo(() => {
    if (!fromFramework || !toFramework) return
    return crosswalks?.filter(
      (cw) =>
        cw.source_framework_id === fromFramework &&
        cw.target_framework_id === toFramework,
    )
  }, [crosswalks, fromFramework, toFramework])
}

export function useCurrentCrosswalk() {
  const [currentCrosswalk, setCurrentCrosswalk] = useAtom(
    currentCrosswalkIdAtom,
  )
  const crosswalks = useCurrentCrosswalks()
  return useMemo(() => {
    const current = crosswalks?.find(({ id }) => id === currentCrosswalk)
    return {
      currentCrosswalk: current,
      setCurrentCrosswalk,
    }
  }, [crosswalks, currentCrosswalk, setCurrentCrosswalk])
}

export function useCreateAlignments() {
  const alignments = useAtomValue(alignmentInputsAtom)
  const { currentCrosswalk } = useCurrentCrosswalk()
  const doRequest = useWithErrorNotification(
    { errorTitle: 'Error Creating Alignments' },
    maintainCrosswalkAlignments,
  )

  return useMemo(() => {
    const canCreate = !!alignments.length && !!currentCrosswalk
    return {
      canCreate,
      numberCreating: alignments?.length ?? 0,
      doCreate: async () => {
        if (!alignments?.length || !currentCrosswalk) return
        await doRequest(currentCrosswalk.id, alignments)
      },
    }
  }, [alignments, currentCrosswalk, doRequest])
}

export function useAlignmentHasMultiple() {
  const alignmentCounts = useAtomValue(alignmentCountsAtom)
  return useCallback(
    (s: { sourceStandardCode: string; targetStandardCode: string }) => {
      const key = alignmentKey(s)
      return alignmentCounts[key] > 1
    },
    [alignmentCounts],
  )
}
